import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Settings2 } from 'lucide-react';
import { IconShare3 } from '@tabler/icons-react';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import PermDisponibilidadeIndexEditor from './PermDisponibilidadeIndexEditor';
import VisibilityEyeBtn from './PermVisibilityEyeBtn';
import PermVisaoGeralSketch from './PermVisaoGeralSketch';
import PermDataInicialPanel from './PermDataInicialPanel';
import {
  isDashWidgetVisible,
  normalizeDashboardVisibility,
  normalizeOverviewVisibility,
  OVERVIEW_MAX_VISIBLE,
  withToggledDashVisibility,
} from './permDashVisibility';
import './PermChamadoTecnicoModal.css';

const LOCATIONS = [
  { name: 'UNIDADE DE DUQUE DE CAXIAS', pct: 9.8, n: 24 },
  { name: 'DR. MARIO GUIMARÃES', pct: 4.1, n: 10 },
  { name: 'RUA DO ROSÁRIO', pct: 3.3, n: 8 },
  { name: 'AVENIDA RIO BRANCO', pct: 3.3, n: 8 },
  { name: 'NITERÓI', pct: 2.9, n: 7 },
];

const EQUIPMENT = [
  { name: '4172', n: 116 },
  { name: 'OKI MB491', n: 37 },
  { name: 'OKI MPS5502MB', n: 25 },
  { name: 'ELGIN L42 PRO', n: 23 },
  { name: 'HP LASERJET PRO MFP', n: 18 },
  { name: 'BROTHER DCP-L5652DN', n: 14 },
  { name: 'EPSON L3250', n: 11 },
  { name: 'OUTROS', n: 9 },
];

const DEFECTS = [
  { name: 'NÃO REGISTRADO', n: 148 },
  { name: 'MÁQUINA TRAVADA…', n: 42 },
  { name: 'ATOLAMENTO', n: 28 },
  { name: 'CÓPIA CLARA-B', n: 18 },
  { name: 'SEM ENERGIA', n: 12 },
  { name: 'OUTROS', n: 8 },
];

const PERIOD_POINTS = [8, 14, 11, 22, 9, 18, 28, 16, 12, 20, 15, 10, 24, 19, 13];
const PERIOD_MAX = 36;

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function StaticToggle({ label, on }) {
  return (
    <span className={`pct-toggle${on ? ' is-on' : ''}`} aria-hidden>
      <span className="pct-toggle-track">
        <span className="pct-toggle-knob" />
      </span>
      <span className="pct-toggle-label">{label}</span>
    </span>
  );
}

function Stars({ value }) {
  const full = Math.floor(value);
  const frac = value - full;
  return (
    <span className="pct-stars" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => {
        let fill = 0;
        if (i < full) fill = 100;
        else if (i === full) fill = Math.round(frac * 100);
        return (
          <span key={i} className="pct-star">
            <span className="pct-star-bg">★</span>
            <span className="pct-star-fg" style={{ width: `${fill}%` }}>
              ★
            </span>
          </span>
        );
      })}
    </span>
  );
}

function LineChartSketch() {
  const w = 320;
  const h = 140;
  const pad = { t: 12, r: 8, b: 28, l: 28 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const pts = PERIOD_POINTS.map((v, i) => {
    const x = pad.l + (i / (PERIOD_POINTS.length - 1)) * iw;
    const y = pad.t + ih - (v / PERIOD_MAX) * ih;
    return `${x},${y}`;
  }).join(' ');
  const avgY = pad.t + ih - (12.3 / PERIOD_MAX) * ih;
  const labels = ['01/04', '13/04', '25/04', '07/05', '19/05', '28/05'];

  return (
    <svg className="pct-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Evolução de chamados">
      {[0, 12, 24, 36].map((tick) => {
        const y = pad.t + ih - (tick / PERIOD_MAX) * ih;
        return (
          <g key={tick}>
            <line x1={pad.l} y1={y} x2={w - pad.r} y2={y} className="pct-chart-grid" />
            <text x={pad.l - 6} y={y + 3} className="pct-chart-axis" textAnchor="end">
              {tick}
            </text>
          </g>
        );
      })}
      <line x1={pad.l} y1={avgY} x2={w - pad.r} y2={avgY} className="pct-chart-avg" />
      <polyline points={pts} className="pct-chart-line" fill="none" />
      {PERIOD_POINTS.map((v, i) => {
        const x = pad.l + (i / (PERIOD_POINTS.length - 1)) * iw;
        const y = pad.t + ih - (v / PERIOD_MAX) * ih;
        return <circle key={i} cx={x} cy={y} r={2.2} className="pct-chart-dot" />;
      })}
      {labels.map((lab, i) => {
        const x = pad.l + (i / (labels.length - 1)) * iw;
        return (
          <text key={lab} x={x} y={h - 8} className="pct-chart-axis" textAnchor="middle">
            {lab}
          </text>
        );
      })}
    </svg>
  );
}

function DonutSketch() {
  const r = 54;
  const c = 2 * Math.PI * r;
  const pct = 0.65;
  return (
    <div className="pct-donut-wrap">
      <svg className="pct-donut" viewBox="0 0 140 140" aria-hidden>
        <circle cx="70" cy="70" r={r} className="pct-donut-track" />
        <circle
          cx="70"
          cy="70"
          r={r}
          className="pct-donut-arc"
          strokeDasharray={`${c * pct} ${c * (1 - pct)}`}
          strokeDashoffset={c * 0.25}
        />
      </svg>
      <div className="pct-donut-center">
        <strong>65%</strong>
        <span>39 dias</span>
        <em>Com chamado</em>
      </div>
    </div>
  );
}

function ParetoSketch() {
  const maxN = Math.max(...DEFECTS.map((d) => d.n));
  const total = DEFECTS.reduce((s, d) => s + d.n, 0);
  let cum = 0;
  const bars = DEFECTS.map((d) => {
    cum += d.n;
    return { ...d, h: (d.n / maxN) * 100, cumPct: (cum / total) * 100 };
  });

  return (
    <div className="pct-pareto">
      <div className="pct-pareto-y">
        <span>160</span>
        <span>80</span>
        <span>0</span>
      </div>
      <div className="pct-pareto-plot">
        <div className="pct-pareto-80" style={{ bottom: '80%' }} />
        <div className="pct-pareto-bars">
          {bars.map((b) => (
            <div key={b.name} className="pct-pareto-col">
              <div className="pct-pareto-bar" style={{ height: `${b.h}%` }} />
            </div>
          ))}
        </div>
        <svg className="pct-pareto-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          <polyline
            fill="none"
            points={bars
              .map((b, i) => {
                const x = ((i + 0.5) / bars.length) * 100;
                const y = 100 - b.cumPct;
                return `${x},${y}`;
              })
              .join(' ')}
          />
        </svg>
        <div className="pct-pareto-x">
          {bars.map((b) => (
            <span key={b.name}>{b.name}</span>
          ))}
        </div>
      </div>
      <div className="pct-pareto-y pct-pareto-y--right">
        <span>100%</span>
        <span>50%</span>
        <span>0%</span>
      </div>
    </div>
  );
}

function DashboardAtendimentosSketch({
  visibility,
  onToggleVisibility,
  onOpenConfig,
  onExport,
  canExport = false,
  busy = false,
}) {
  const equipMax = Math.max(...EQUIPMENT.map((e) => e.n));
  const locMax = Math.max(...LOCATIONS.map((l) => l.pct));
  const showRating = isDashWidgetVisible(visibility, 'qualityRating');
  const showLocations = isDashWidgetVisible(visibility, 'locations');
  const showPeriod = isDashWidgetVisible(visibility, 'period');
  const showAvailability = isDashWidgetVisible(visibility, 'availability');
  const showEquipment = isDashWidgetVisible(visibility, 'equipment');
  const showDefects = isDashWidgetVisible(visibility, 'defects');

  return (
    <div className="pct-dash" aria-label="Esboço do Dashboard de atendimentos">
      <header className="pct-dash-head">
        <div className="pct-dash-head-left">
          <h2 className="pct-dash-title">Dashboard de atendimentos</h2>
          <p className="pct-dash-sub">245 chamados com os filtros atuais da tabela</p>
        </div>
        <div className="pct-dash-head-right">
          <div className="pct-dash-config-group" role="group" aria-label="Configuração do dashboard">
            <button
              type="button"
              className="pct-dash-config-btn"
              onClick={onOpenConfig}
              disabled={busy}
            >
              <Settings2 size={14} strokeWidth={2} aria-hidden />
              Configurar o que é exibido
            </button>
            <span className="pct-dash-config-sep" aria-hidden />
            <GoHoverTooltip
              text={
                canExport
                  ? 'Exportar configuração para outros contratos'
                  : 'Salve uma configuração antes de exportar'
              }
              align="end"
              className="go-hover-tip-wrap--contents"
              zIndex={13050}
            >
              <button
                type="button"
                className="pct-dash-config-btn pct-dash-export-btn"
                onClick={onExport}
                disabled={busy || !canExport}
                aria-label="Exportar configuração para outros contratos"
              >
                <IconShare3 size={14} stroke={1.9} aria-hidden />
                <span className="pct-dash-export-q" aria-hidden>
                  ?
                </span>
              </button>
            </GoHoverTooltip>
          </div>
        </div>
      </header>

      <div className="pct-dash-grid">
        <section className={`pct-card${showLocations ? '' : ' is-hidden'}`}>
          <div className="pct-card-head">
            <div>
              <h3 className="pct-card-title">Chamados entre locais</h3>
              <p className="pct-card-sub">Principais locais (percentual sobre o total)</p>
            </div>
            <div className="pct-card-tools">
              {showLocations ? <StaticToggle label="Ver outros locais" /> : null}
              <VisibilityEyeBtn
                label="Chamados entre locais"
                visible={showLocations}
                disabled={busy}
                onClick={() => onToggleVisibility?.('locations')}
              />
            </div>
          </div>
          <div className="pct-card-body">
            <ul className="pct-loc-list">
              {LOCATIONS.map((loc) => (
                <li key={loc.name} className="pct-loc-row">
                  <span className="pct-loc-name">{loc.name}</span>
                  <div className="pct-loc-bar-track">
                    <div className="pct-loc-bar" style={{ width: `${(loc.pct / locMax) * 100}%` }} />
                  </div>
                  <span className="pct-loc-meta">
                    {String(loc.pct).replace('.', ',')}% <em>({loc.n})</em>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {!showLocations ? <span className="pct-not-shown-banner">Não exibido</span> : null}
        </section>

        <section className={`pct-card${showPeriod ? '' : ' is-hidden'}`}>
          <div className="pct-card-head">
            <div>
              <h3 className="pct-card-title">Chamados no período</h3>
              <p className="pct-card-sub">Evolução diária ou por intervalo resumido (Média: 12,3)</p>
            </div>
            <VisibilityEyeBtn
              label="Chamados no período"
              visible={showPeriod}
              disabled={busy}
              onClick={() => onToggleVisibility?.('period')}
            />
          </div>
          <div className="pct-card-body">
            <LineChartSketch />
          </div>
          {!showPeriod ? <span className="pct-not-shown-banner">Não exibido</span> : null}
        </section>

        <div className="pct-dash-donut-col">
          <div className={`pct-dash-rating${showRating ? '' : ' is-hidden'}`}>
            <VisibilityEyeBtn
              label="Avaliações de qualidade"
              visible={showRating}
              disabled={busy}
              onClick={() => onToggleVisibility?.('qualityRating')}
            />
            <div className="pct-dash-rating-body" aria-hidden={!showRating}>
              <span className="pct-dash-rating-label">Avaliações de qualidade</span>
              <Stars value={4.15} />
              <strong className="pct-dash-rating-val">4,15</strong>
            </div>
            {!showRating ? <span className="pct-not-shown-badge">Não exibido</span> : null}
          </div>
          <section className={`pct-card pct-card--donut${showAvailability ? '' : ' is-hidden'}`}>
            <div className="pct-card-head">
              <div>
                <h3 className="pct-card-title">Índice de disponibilidade</h3>
                <p className="pct-card-sub">60 dias no período filtrado — sem e com chamado</p>
              </div>
              <VisibilityEyeBtn
                label="Índice de disponibilidade"
                visible={showAvailability}
                disabled={busy}
                onClick={() => onToggleVisibility?.('availability')}
              />
            </div>
            <div className="pct-card-body">
              <DonutSketch />
            </div>
            {!showAvailability ? <span className="pct-not-shown-banner">Não exibido</span> : null}
          </section>
        </div>

        <section className={`pct-card pct-card--wide pct-card--equip${showEquipment ? '' : ' is-hidden'}`}>
          <div className="pct-card-head">
            <div>
              <h3 className="pct-card-title">Chamados por equipamento</h3>
              <p className="pct-card-sub">Por modelo — maior volume no topo</p>
            </div>
            <div className="pct-card-tools">
              {showEquipment ? <StaticToggle label="Ver séries" /> : null}
              <VisibilityEyeBtn
                label="Chamados por equipamento"
                visible={showEquipment}
                disabled={busy}
                onClick={() => onToggleVisibility?.('equipment')}
              />
            </div>
          </div>
          <div className="pct-card-body">
            <ul className="pct-equip-list">
              {EQUIPMENT.map((eq) => (
                <li key={eq.name} className="pct-equip-row">
                  <span className="pct-equip-name">{eq.name}</span>
                  <div className="pct-equip-bar-track">
                    <div className="pct-equip-bar" style={{ width: `${(eq.n / equipMax) * 100}%` }} />
                    <span className="pct-equip-n">{eq.n}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {!showEquipment ? <span className="pct-not-shown-banner">Não exibido</span> : null}
        </section>

        <section className={`pct-card pct-card--wide pct-card--defects${showDefects ? '' : ' is-hidden'}`}>
          <div className="pct-card-head">
            <div>
              <h3 className="pct-card-title">Problemas frequentes</h3>
              <p className="pct-card-sub">Defeito × chamados — linha de 80%</p>
            </div>
            <VisibilityEyeBtn
              label="Problemas frequentes"
              visible={showDefects}
              disabled={busy}
              onClick={() => onToggleVisibility?.('defects')}
            />
          </div>
          <div className="pct-card-body">
            <ParetoSketch />
          </div>
          {!showDefects ? <span className="pct-not-shown-banner">Não exibido</span> : null}
        </section>
      </div>
    </div>
  );
}

/**
 * Modal de configuração de Chamado técnico (esboço).
 * Identifica o cliente por contrato + nome; abas no estilo do painel técnico.
 */
export default function PermChamadoTecnicoModal({
  open,
  onClose,
  contrato,
  cliente,
  codigo,
  techCallCfg,
  onSaveTechCallCfg,
  onExportConfig,
  overviewVisibility,
  onSaveOverviewVisibility,
  dataStartDates,
  onSaveDataStartDate,
  saving = false,
}) {
  const titleId = useId();
  const [editPanel, setEditPanel] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!open) {
      setEditPanel(null);
      setTab('overview');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (editPanel) {
        setEditPanel(null);
        return;
      }
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, editPanel]);

  if (!open) return null;

  const clienteLabel = cliente || 'Cliente';
  const contratoLabel = contrato || '—';
  const ariaLabel = `Chamado técnico — ${clienteLabel} · ${contratoLabel}`;
  const visibility = normalizeDashboardVisibility(techCallCfg);

  const toggleVisibility = async (key) => {
    if (saving) return;
    const nextCfg = withToggledDashVisibility(techCallCfg, key);
    await onSaveTechCallCfg?.(nextCfg);
  };

  const overview = normalizeOverviewVisibility(overviewVisibility);
  const toggleOverview = async (key) => {
    if (saving) return;
    const next = { ...overview, [key]: overview[key] === 1 ? 0 : 1 };
    if (Object.values(next).filter((v) => v === 1).length > OVERVIEW_MAX_VISIBLE) return;
    await onSaveOverviewVisibility?.(next);
  };

  const TABS = [
    { id: 'overview', label: 'Visão geral' },
    { id: 'dash', label: 'Dashboard Atendimentos' },
    { id: 'dataStart', label: 'Data inicial de dados' },
  ];

  return createPortal(
    <div
      className="pct-overlay tp-layout-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-label={ariaLabel}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={`pct-shell${editPanel ? ' has-editor' : ''}`}>
        {!editPanel ? (
          <GoHoverTooltip text="Fechar" align="center" className="go-hover-tip-wrap--contents" zIndex={13050}>
            <button type="button" className="pct-close" aria-label="Fechar" onClick={onClose}>
              <svg width="12" height="12" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" fill="none">
                <path d="M5 5l14 14M19 5L5 19" />
              </svg>
            </button>
          </GoHoverTooltip>
        ) : null}

        <div className="pct-root">
          <header className="pct-header">
            <div className="pct-avatar" aria-hidden>
              {initialsFromName(clienteLabel)}
            </div>
            <div className="pct-id">
              <div className="pct-id-row">
                <span className="pct-name" id={titleId}>
                  {clienteLabel}
                </span>
                <span className="pct-badge" title={`Contrato ${contratoLabel}`}>
                  {contratoLabel}
                </span>
                {codigo != null && String(codigo).trim() !== '' ? (
                  <span className="pct-badge pct-badge--muted" title="Código">
                    #{codigo}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="pct-flex" />
            <nav className="pct-tabs" aria-label="Abas de exibições">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`pct-tab${tab === t.id ? ' is-active' : ''}`}
                  aria-current={tab === t.id ? 'page' : undefined}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                  <span className="pct-ul" aria-hidden />
                </button>
              ))}
            </nav>
          </header>

          <div className="pct-content">
            {tab === 'overview' ? (
              <PermVisaoGeralSketch visibility={overview} busy={saving} onToggleVisibility={toggleOverview} />
            ) : tab === 'dataStart' ? (
              <PermDataInicialPanel
                values={dataStartDates}
                busy={saving}
                onSave={(key, value) => onSaveDataStartDate?.(key, value)}
              />
            ) : (
              <DashboardAtendimentosSketch
                visibility={visibility}
                busy={saving}
                onOpenConfig={() => setEditPanel('dashDisplay')}
                onToggleVisibility={toggleVisibility}
                canExport={(!!techCallCfg && typeof techCallCfg === 'object') || !!overviewVisibility}
                onExport={() => onExportConfig?.()}
              />
            )}
          </div>
        </div>

        <PermDisponibilidadeIndexEditor
          open={editPanel === 'dashDisplay'}
          onClose={() => setEditPanel(null)}
          techCallCfg={techCallCfg}
          busy={saving}
          onSave={async (nextCfg) => {
            await onSaveTechCallCfg?.(nextCfg);
            setEditPanel(null);
          }}
        />
      </div>
    </div>,
    document.body
  );
}
